import { PayerRow } from "@finpay-development/shared-platform/lib/esm/admin-configuration/models/payers";
import { Dispatch } from "@reduxjs/toolkit";
import { Estimate, Payor, PayorPlan, Plan, Vob } from "@finpay-estimation/estimation-types";
import { PatientCurrentInsurance, PatientViewModel } from "@finpay-development/shared-platform/lib/esm/patient/components/models/patient-view-model";
import { ClientStatusCardViewModel } from "@finpay-development/shared-platform/lib/esm/shared/model/client-status-card";
import { setSelectedVob } from "@finpay-development/shared-platform/lib/esm/shared/state/vob-slice";

/**
 * Finds the self-pay payor from the provided payors map
 * @param payorsMap - Map of payors keyed by payorId
 * @returns The self-pay payor or undefined if not found
 */
export const findSelfPayPayor = (payorsMap: Map<number, PayerRow>): PayerRow | undefined => {
  return Array.from(payorsMap.values()).find((payor) => {
    return payor.payorName.toLowerCase() === 'self-pay';
  });
};

/**
 * Checks if the provided payorId is for a self-pay payor
 * @param payorId - The payor ID to check
 * @param payorsMap - Map of payors keyed by payorId
 * @returns True if the payor is a self-pay payor, false otherwise
 */
export const isSelfPayPayor = (payorId: number | undefined, payorsMap: Map<number, PayerRow>): boolean => {
  if (!payorId) return false;
  
  const selfPayPayor = findSelfPayPayor(payorsMap);
  return selfPayPayor?.payorId === payorId;
}; 

export const normalizeDescription = (
  desc: string = '',
  isPrivate: boolean,
  isSelf: boolean
): string => {
  // Remove any existing prefixes first to avoid doubles
  let cleaned = desc.replace(/^(Private Pay - |Self Pay - )/, '');

  if (isPrivate) {
      return `Private Pay - ${cleaned}`;
  }

  if (isSelf) {
      return `Self Pay - ${cleaned}`;
  }

  return cleaned;
};

export async function loadAndSetRateAsWithoutVob(opts: {
    selectedPatient: PatientViewModel;
    patientDemographicResp?: any;
    selectedPatientCurrentInsurance?: PatientCurrentInsurance;
    patientInsuranceResp?: any;
    allClientsMap: Map<number, ClientStatusCardViewModel>;
    dispatch: Dispatch<any>;
    payers: PayerRow[];
    loadAndSetRateAsPayorPlan: (
        facility: any,
        dispatch: Dispatch<any>,
        payers: any,
        payor: Payor,
        plan: Plan,
        estimate?: Estimate,
        rateAsPayorPlan?: PayorPlan
    ) => Promise<void>;
}): Promise<boolean> {
    const {
        selectedPatient,
        patientDemographicResp,
        selectedPatientCurrentInsurance,
        patientInsuranceResp,
        allClientsMap,
        dispatch,
        payers,
        loadAndSetRateAsPayorPlan,
    } = opts;

    // 1) Get clientId (prefer selectedPatient, else demographics)
    const clientIdFromSelected = Number(selectedPatient?.clientId ?? 0);
    const clientIdFromDemo = Number(
        patientDemographicResp?.payload?.patientDetails?.clientId ?? 0
    );
    const targetClientId =
        clientIdFromSelected !== 0 ? clientIdFromSelected : clientIdFromDemo;
    if (!targetClientId || !allClientsMap?.size) return false;

    // 2) Choose insurance (prefer selected current insurance, else first from list)
    const fallbackInsurance = patientInsuranceResp?.payload?.[0];
    const targetInsurance =
        selectedPatientCurrentInsurance?.patientCurrentInsuranceId! > 0
            ? selectedPatientCurrentInsurance
            : fallbackInsurance;
    if (!targetInsurance?.payorId || !targetInsurance?.payorPlanId)
        return false;

    // 3) Resolve facility (first facility on the client)
    const targetClient = allClientsMap.get(Number(targetClientId));
    const targetFacility = targetClient?.clientFacilities?.[0];
    if (!targetFacility) return false;

    // 4) Call existing loader
    const selectedPayor: Payor = {payorId: targetInsurance.payorId};
    const selectedPlan: Plan = {payorPlanId: targetInsurance.payorPlanId};

    await loadAndSetRateAsPayorPlan(
        targetFacility,
        dispatch,
        payers,
        selectedPayor,
        selectedPlan
    );
    const currentYear = new Date().getFullYear();
    const newVob: Vob = {
      "patientId": selectedPatient.patientId,
      "client": {
          "clientId":  Number(selectedPatient.clientId),
      },
      "facility": targetFacility,
      "payor": selectedPayor,
      "plan": selectedPlan,
      "vobSourceId": 4, // manual
      "isValid": false,
      "digitalVerificationMethod": false,
      "liveVOB": false,
      "activePolicy": false,
      "isCarryover": false,
      "isCopayReset": false,
      "policyBeginDate": `${currentYear}-01-01`,
      "policyEndDate": `${currentYear}-12-31`,
  } as Vob;

  dispatch(setSelectedVob(newVob));

    return true;
}
